/**
 * TableWithHeader - Reusable wrapper for tables with standardized header and expand/collapse
 *
 * Combines DataTableWrapper with a consistent header pattern that includes:
 * - Title heading
 * - Expand/collapse button (when rows exceed maxRows)
 *
 * This component eliminates code duplication across table components.
 *
 * Usage:
 * ```tsx
 * <TableWithHeader title="My Table" totalRows={data.length}>
 *   <ReusableTable columns={columns} data={data} />
 * </TableWithHeader>
 * ```
 */

import { Button, Heading, HStack, type TableRowProps } from '@chakra-ui/react';
import type React from 'react';

import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { DataTableWrapper } from '~/components/tables/DataTableWrapper';

export interface TableWithHeaderProps<T = unknown> {
  /** Table title to display in header */
  title: string;

  /** Total number of rows in the dataset */
  totalRows: number;

  /** Maximum number of rows to show in collapsed mode (default: LAYOUT_SPACING.TABLE_DEFAULT_ROWS) */
  maxRows?: number;

  /** Table element (must be ReactElement with data prop, typically ReusableTable) */
  children: React.ReactElement<{
    data: T[];
    footerRow?: T;
    footerRowStyle?: TableRowProps;
    columns?: unknown[];
  }>;

  /** Optional custom header render function (overrides default header) */
  renderHeader?: (isExpanded: boolean, toggle: () => void) => React.ReactNode;
}

/**
 * TableWithHeader Component
 *
 * Provides a consistent header pattern for tables with expand/collapse functionality.
 * Reduces code duplication across DateTable, TaxableValueTable, and ExistingDebtCumulativeTable.
 */
export const TableWithHeader = <
  T extends React.JSXElementConstructor<React.ReactNode> | keyof React.JSX.IntrinsicElements,
>({
  title,
  totalRows,
  maxRows = LAYOUT_SPACING.TABLE_DEFAULT_ROWS,
  children,
  renderHeader,
}: TableWithHeaderProps<T>) => {
  const defaultHeader = (isExpanded: boolean, toggle: () => void) => (
    <HStack justify="space-between" align="center" mb={4}>
      <Heading size="md">{title}</Heading>
      {totalRows > maxRows && (
        <Button
          size="sm"
          variant="outline"
          onClick={toggle}
          colorPalette="blue"
          transition={`all ${LAYOUT_SPACING.TRANSITION_FAST}`}
        >
          {isExpanded ? `Show Less (${maxRows} rows)` : `Show All (${totalRows} rows)`}
        </Button>
      )}
    </HStack>
  );

  return (
    <DataTableWrapper
      totalRows={totalRows}
      collapsedRowCount={maxRows}
      buttonPlacement="both"
      renderHeader={renderHeader || defaultHeader}
    >
      {children}
    </DataTableWrapper>
  );
};
